import { ChakraProvider } from '@chakra-ui/react'
import { MDXProvider } from '@mdx-js/react'
import MarkdownImage from 'components/markdown/components/MarkdownImage'
import MarkdownLink from 'components/markdown/components/MarkdownLink'
import MarkdownStrong from 'components/markdown/components/MarkdownStrong'
import MarkdownTable from 'components/markdown/components/MarkdownTable'
import MarkdownTd from 'components/markdown/components/MarkdownTd'
import MarkdownTh from 'components/markdown/components/MarkdownTh'
import OmensSeoHead, { OmensSeoContext } from 'components/meta/OmensSeoHead'
import { GtmNoscript } from 'components/scripts/GtmNoscript'
import { InsiderScript } from 'components/scripts/InsiderScript'
import { Body1, H1, H2 } from 'components/typography'
import { CUIDEI_BRAND_COOKIE } from 'constants/cuidei'
import { IS_PRODUCTION } from 'constants/isDevelopment'
import { useIsCuideiDomain } from 'hooks/helpers/useIsCuideiPage'
import { useIsMedlevePage } from 'hooks/helpers/useIsMedlevePage'
import EmptyLayout from 'layouts/EmptyLayout'
import { MDXComponents } from 'mdx/types'
import { ensureCuideiTagManager, isCuideiTagDocument } from 'modules/cuidei/PathologyLanding/CuideiTagManager'
import { useCuideiTagBoundary } from 'modules/cuidei/PathologyLanding/tagBoundary'
import { isCuideiHost } from 'modules/cuidei/technicalSeo/host'
import { resolveOmensPageSeo } from 'modules/omensSeoTechnical/head'
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { FC, useEffect } from 'react'
import TagManager from 'react-gtm-module'
import { Provider } from 'react-redux'
import { store } from 'redux/store'
import { persistStore } from 'redux-persist'
import { PersistGate } from 'redux-persist/integration/react'
import { loadDeviceFingerprintOnFirstInteraction } from 'services/device-fingerprint'
import Fonts from 'styles/fonts'
import theme from 'styles/theme'
import { markAppHydrated } from 'utils/appHydration'
import { getCookies, setCookies } from 'utils/cookieHelpers'
import { captureLivePreviewToken } from 'utils/livePreview'
import { SsrContentContext } from 'utils/ssrContent'

export type NextPageWithLayout = NextPage & {
  Layout?: FC<any>
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const mdxComponents: MDXComponents = {
  img: (props) => <MarkdownImage {...props} />,
  a: (props) => <MarkdownLink {...props} />,
  h1: (props) => <H1 {...props} />,
  h2: (props) => <H2 {...props} />,
  p: (props) => <Body1 {...props} />,
  strong: (props) => <MarkdownStrong {...props} />,
  td: (props) => <MarkdownTd {...props} />,
  th: (props) => <MarkdownTh {...props} />,
  table: (props) => <MarkdownTable {...props} />
}

const persistor = persistStore(store)

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const Layout = Component.Layout || EmptyLayout
  const { favicon } = pageProps
  // Read directly, not through the hydration-safe hook: it decides which GTM container starts, on the first render.
  const isIshaPage = Boolean(getCookies('isIshaAddress')?.isValid)
  const isCuideiDomain = useIsCuideiDomain()
  const isMedlevePage = getCookies('isMedleveAddress')?.isValid
  const router = useRouter()
  useCuideiTagBoundary(router)
  // Cuidei brand signal for the document head: cookie (set on QR/landing entry) or
  // the Cuidei routes themselves, so the favicon is correct on the very first paint.
  const isCuideiPage =
    isCuideiDomain ||
    Boolean(getCookies(CUIDEI_BRAND_COOKIE)?.isValid) ||
    router.pathname.startsWith('/farma') ||
    router.pathname.startsWith('/patologia')

  // Prevent "removeChild" crashes caused by third-party scripts (Insider, GTM, extensions)
  // modifying the DOM outside React's control. See: https://github.com/facebook/react/issues/11538
  useEffect(() => {
    if (typeof Node !== 'undefined') {
      const origRemoveChild = Node.prototype.removeChild
      // @ts-ignore
      Node.prototype.removeChild = function <T extends Node>(child: T): T {
        if (child.parentNode !== this) {
          return child
        }
        // @ts-ignore
        return origRemoveChild.apply(this, arguments)
      }

      const origInsertBefore = Node.prototype.insertBefore
      // @ts-ignore
      Node.prototype.insertBefore = function <T extends Node>(newNode: T, refNode: Node | null): T {
        if (refNode && refNode.parentNode !== this) {
          return newNode
        }
        // @ts-ignore
        return origInsertBefore.apply(this, arguments)
      }
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname
      const ishaCookie = getCookies('isIshaAddress')
      const isMedleve = getCookies('isMedleveAddress')

      if (hostname.includes('souisha.') && !ishaCookie.isValid) {
        setCookies({ name: 'isIshaAddress', value: 'true' })
      }
      if (hostname.includes('medleve.') && !isMedleve.isValid) {
        setCookies({ name: 'isMedleveAddress', value: 'true' })
      }

      // Pre-launch live-attendance testing: persist ?live=<token> so the live
      // option is enabled for this visitor throughout the flow.
      captureLivePreviewToken()
    }
  }, [])

  // First hydration over (after every child's effects): later mounts have no server HTML (HydrateWhenVisible).
  useEffect(markAppHydrated, [])

  // Load a stable device fingerprint once for anti-fraud signal capture (on the first interaction).
  useEffect(() => loadDeviceFingerprintOnFirstInteraction(), [])

  // Tag <html> on Cuidei pages so brand-scoped global styles (e.g. the blue
  // scrollbar instead of the Omens yellow) apply. See theme global styles.
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (isCuideiPage) document.documentElement.setAttribute('data-brand', 'cuidei')
    else document.documentElement.removeAttribute('data-brand')
  }, [isCuideiPage])

  // Every page on a Cuidei host carries ONLY the Cuidei GTM container: no
  // site-wide Omens GTM, Insider or any other tag there. _document renders it
  // for the Cuidei routes (hub, /<slug>, landing + flow) and for the shared
  // pages (/farma/*, legal) requested on a Cuidei host; anything else on the
  // host (the 404 page, stray Omens routes) gets it client-side.
  const isCuideiTags = isCuideiTagDocument(router.pathname, pageProps)

  // Omens pages: title, description, canonical, Open Graph and site JSON-LD from the registry, in the server HTML.
  const omensSeo = resolveOmensPageSeo({
    pathname: router.pathname,
    asPath: router.asPath,
    pageProps,
    hostname: typeof window === 'undefined' ? undefined : window.location.hostname
  })
  const isOnCuideiHost = typeof window !== 'undefined' && isCuideiHost(window.location.hostname)

  useEffect(() => {
    if (isCuideiHost(window.location.hostname)) {
      if (IS_PRODUCTION) ensureCuideiTagManager()
      return
    }
    if (isCuideiTags) return
    if (IS_PRODUCTION && process.env.NEXT_PUBLIC_GOOGLE_MANAGER_ID)
      if (isIshaPage && IS_PRODUCTION && process.env.NEXT_PUBLIC_GOOGLE_MANAGER_ID_ISHA) {
        TagManager.initialize({
          gtmId: process.env.NEXT_PUBLIC_GOOGLE_MANAGER_ID_ISHA
        })
      } else
        TagManager.initialize({
          gtmId: process.env.NEXT_PUBLIC_GOOGLE_MANAGER_ID
        })
  }, [isIshaPage, isMedlevePage, isCuideiTags])

  // Cleanup Action Cable connection when tab/window closes
  useEffect(() => {
    const handleBeforeUnload = () => {
      import('utils/actioncableHelper').then(({ disconnectActionCable }) => {
        disconnectActionCable()
      })
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  const ssrContent = pageProps.medleveOrganic === true || pageProps.ssrContent === true

  const app = (
    <OmensSeoContext.Provider value={omensSeo}>
      <MDXProvider components={mdxComponents}>
        <ChakraProvider theme={theme}>
          <Layout>
            <Component {...pageProps} />
          </Layout>
        </ChakraProvider>
      </MDXProvider>
    </OmensSeoContext.Provider>
  )

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        {isCuideiPage && (
          <>
            {/* Chrome keeps a separate, long-lived favicon cache per origin.
                Use the standard rel plus a versioned URL so Cuidei does not
                inherit a previously cached Omens icon on cuidei.com.br. */}
            <link rel="icon" type="image/png" sizes="256x256" href="/favicon-cuidei.png?v=20260710" />
            <link rel="shortcut icon" href="/favicon-cuidei.png?v=20260710" />
            <link rel="apple-touch-icon" href="/cuidei/cuidei-apple-touch-icon.png?v=20260710" />
          </>
        )}
        {isMedlevePage && !isCuideiPage && !isIshaPage && <link rel="shortcut icon" href="/medleve-favicon.png" />}
        {!isIshaPage && !isCuideiPage && !isMedlevePage && (
          <link rel="shortcut icon" href={favicon || '/favicon.png'} />
        )}
        {isIshaPage && !isCuideiPage && <link rel="shortcut icon" href="/favicon-isha.png" />}
      </Head>
      {omensSeo && <OmensSeoHead seo={omensSeo} />}

      <Fonts />

      {/* MedLeve organic pages flag themselves with `medleveOrganic: true` (Cuidei pathology landings with
          `ssrContent: true`) from getServerSideProps and render without the client-only PersistGate, so their
          content is in the server HTML (redux still rehydrates after mount). Every other page has no such prop
          and keeps the gate exactly as before. */}
      <Provider store={store}>
        {ssrContent ? (
          <SsrContentContext.Provider value>{app}</SsrContentContext.Provider>
        ) : (
          <PersistGate loading={null} persistor={persistor}>
            {app}
          </PersistGate>
        )}
      </Provider>

      {/* TODO: Uncomment on launch*/}
      {IS_PRODUCTION && !isCuideiTags && (
        <>
          {/* Renders no markup (next/script), so the client-only host check cannot break hydration. */}
          {!isOnCuideiHost && <InsiderScript />}
          {process.env.NEXT_PUBLIC_GOOGLE_MANAGER_ID && process.env.NEXT_PUBLIC_GOOGLE_MANAGER_ID_ISHA && (
            <GtmNoscript
              gtmId={
                isIshaPage ? process.env.NEXT_PUBLIC_GOOGLE_MANAGER_ID_ISHA : process.env.NEXT_PUBLIC_GOOGLE_MANAGER_ID
              }
            />
          )}
        </>
      )}
    </>
  )
}
